import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  Link,
} from '@mui/material';
import { importerCardStyles } from './styles';
import { Edit, Delete, AddAPhoto, NoPhotography, Bookmark, BookmarkRemove, BookmarkAdd } from '@mui/icons-material';
import { PersonIcon, NotesIcon, CompanyIcon, UserIcon } from 'components/Shared/Icons/tooltip_icons';
import { Importer } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditImporter from './AddOrEditImporter';
import PersonCard from '../people/PersonCard';
import CompanyCard from '../companies/CompanyCard';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import UserCard from 'pages/users/list/UserCard';

interface Props {
  option: Importer;
  showActionButtons: boolean;
  onImporterDelete?: () => void;
  onImporterUpdate?: () => void;
}

const ImporterCard = (props: Props) => {
  const { option: importer, showActionButtons = true, onImporterDelete, onImporterUpdate } = props;

  const isMobileView = useMediaQuery('(max-width:600px)');

  const [editImporterModalOpen, setEditImporterModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>();

  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  const [deleteImageDialogOpen, setDeleteImageDialogOpen] = useState(false);
  const [deletePOADialogOpen, setDeletePOADialogOpen] = useState(false);

  const deleteImporter = () => {
    axiosService
      .delete(`/entities/importer/${importer.id}/`)
      .then(() => {
        if (onImporterDelete) {
          onImporterDelete();
        }
      })
      .catch(error => setError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onImporterEdit = () => {
    if (onImporterUpdate) {
      onImporterUpdate();
    }
    setEditImporterModalOpen(false);
  };

  const uploadImporterPicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Construct form data
    const formData = new FormData();
    const { files } = e.target;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i += 1) {
        formData.append('picture', files[i]);
      }
    }

    // Make a POST request to upload the picture
    axiosService
      .patch(`/entities/importer/${importer?.id}/`, formData)
      .then(onImporterUpdate)
      .catch(err => setError(err.message));
  };

  const removeImporterPhoto = () => {
    // Make a POST request to remove the picture
    axiosService
      .patch(`/entities/importer/${importer?.id}/`, { picture: null })
      .then(onImporterUpdate)
      .catch(err => setError(err.message));
  };

  const uploadImporterPOA = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Construct form data
    const formData = new FormData();
    const { files } = e.target;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i += 1) {
        formData.append('poa', files[i]);
      }
    }

    // Make a POST request to upload the POA file
    axiosService
      .patch(`/entities/importer/${importer?.id}/`, formData)
      .then(onImporterUpdate)
      .catch(err => setError(err.message));
  };

  const removeImporterPOA = () => {
    // Make a POST request to remove the POA file
    axiosService
      .patch(`/entities/importer/${importer?.id}/`, { poa: null })
      .then(onImporterUpdate)
      .catch(err => setError(err.message));
  };

  const getCardTitle = () => {
    if (importer.person && importer.company) {
      return `${importer.person.first_name} ${importer.person.last_name} - ${importer.company.name}`;
    } else if (importer.person) {
      return `${importer.person.first_name} ${importer.person.last_name}`;
    } else if (importer.company) {
      return importer.company.name;
    }
    return '';
  };

  return (
    <>
      <Card elevation={1} sx={importerCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                {importer?.poa ? (
                  <Tooltip title="Delete POA">
                    <IconButton
                      sx={importerCardStyles.cardAction}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeletePOADialogOpen(true);
                      }}
                    >
                      <BookmarkRemove />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Upload POA">
                    <IconButton sx={importerCardStyles.cardAction} component="label">
                      <BookmarkAdd />
                      <input
                        type="file"
                        accept="image/png, image/jpg, image/jpeg, application/pdf"
                        hidden
                        onInput={uploadImporterPOA}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {importer?.picture ? (
                  <Tooltip title="Delete photo">
                    <IconButton
                      sx={importerCardStyles.cardAction}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteImageDialogOpen(true);
                      }}
                    >
                      <NoPhotography />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Add a photo (ID)">
                    <IconButton sx={importerCardStyles.cardAction} component="label">
                      <AddAPhoto />
                      <input
                        type="file"
                        accept="image/png, image/jpg, image/jpeg"
                        hidden
                        onInput={uploadImporterPicture}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <IconButton
                    sx={importerCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditImporterModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={importerCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={getCardTitle()} tooltipText={importer.id} />}
          subheader="Importer"
          sx={importerCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={importerCardStyles.contentBox}>
            <Box sx={importerCardStyles.columnPhoto}>
              {importer?.picture ? (
                <img
                  src={`${process.env.REACT_APP_MEDIA_API_URL}/${importer?.picture}`}
                  alt={`${process.env.REACT_APP_MEDIA_API_URL}/${importer?.picture}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: '150px',
                    objectFit: 'contain',
                    objectPosition: isMobileView ? 'center' : 'center 0px',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  }}
                  onClick={() => setIsImageViewerOpen(true)}
                />
              ) : (
                <NoPhotography sx={{ width: 30, height: 30 }} />
              )}
            </Box>
            <Box sx={importerCardStyles.columnDetails}>
              {importer.user ? (
                <Box sx={importerCardStyles.contactBox}>
                  <UserIcon />
                  <Tooltip
                    title={importer.user ? <UserCard option={importer.user} showActionButtons={false} /> : null}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent',
                          maxWidth: 'none',
                        },
                      },
                    }}
                  >
                    <Typography color="text" sx={importerCardStyles.contactValue}>
                      {`${importer.user.first_name} ${importer.user.last_name}`}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : null}
              {importer.person ? (
                <Box sx={importerCardStyles.contactBox}>
                  <PersonIcon />
                  <Tooltip
                    title={importer.person ? <PersonCard option={importer.person} showActionButtons={false} /> : null}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent',
                          maxWidth: 'none',
                        },
                      },
                    }}
                  >
                    <Typography color="text" sx={importerCardStyles.contactValue}>
                      {`${importer.person.first_name} ${importer.person.last_name}`}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : null}
              {importer.company ? (
                <Box sx={importerCardStyles.contactBox}>
                  <CompanyIcon />
                  <Tooltip
                    title={
                      importer.company ? <CompanyCard option={importer.company} showActionButtons={false} /> : null
                    }
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent',
                          maxWidth: 'none',
                        },
                      },
                    }}
                  >
                    <Typography color="text" sx={importerCardStyles.contactValue}>
                      {importer.company.name}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : null}
              <Box sx={importerCardStyles.contactBox}>
                <Tooltip title="POA">
                  <Bookmark />
                </Tooltip>
                <Typography color="text" sx={importerCardStyles.contactValue}>
                  {importer?.poa ? (
                    <Link href={`${process.env.REACT_APP_MEDIA_API_URL}/${importer?.poa}`} target="_blank">
                      POA
                    </Link>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
              <Box sx={importerCardStyles.contactBox}>
                <NotesIcon />
                <Typography color="text" sx={importerCardStyles.contactValue}>
                  {importer.notes || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete importer</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete importer <b>{getCardTitle()}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteImporter} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditImporter
        importer={importer}
        onSave={onImporterEdit}
        modalTitle="Edit importer"
        isModalOpen={editImporterModalOpen}
        setModalOpen={setEditImporterModalOpen}
      />
      <Dialog open={deletePOADialogOpen} onClose={() => setDeletePOADialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete POA</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the importer&#39;s POA?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePOADialogOpen(false)}>Cancel</Button>
          <Button onClick={removeImporterPOA} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteImageDialogOpen} onClose={() => setDeleteImageDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete photo</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the importer&#39;s photo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteImageDialogOpen(false)}>Cancel</Button>
          <Button onClick={removeImporterPhoto} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Lightbox
        open={isImageViewerOpen}
        close={() => setIsImageViewerOpen(false)}
        slides={importer?.picture ? [{ src: `${process.env.REACT_APP_MEDIA_API_URL}/${importer?.picture}` }] : []}
        index={0}
        plugins={[Zoom, Download, Fullscreen]}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup title={error?.statusText} message={error?.message} onClose={() => setError(null)} />
    </>
  );
};

export default ImporterCard;
